import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import MarketingShell from "./MarketingShell";
import { PaperPlaneTilt, UsersThree, Upload, ArrowsClockwise, Stack, Clock, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatLocal, formatLocalDate } from "@/lib/timezone";

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="bg-white border border-[#E5E5E5] p-5" data-testid={testid}>
      <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">{label}</p>
      <p className="text-3xl font-black text-[#0A0A0A] mt-1 tabular-nums" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{value}</p>
      {sub && <p className="text-xs text-[#525252] mt-1">{sub}</p>}
    </div>
  );
}

export default function MarketingOverview() {
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [insights, setInsights] = useState(null);
  const [queue, setQueue] = useState({ items: [], total: 0 });
  const [queueLoading, setQueueLoading] = useState(false);

  const reload = async () => {
    try {
      const [s, camps, ins] = await Promise.all([
        api.get("/marketing/contacts/_stats"),
        api.get("/marketing/campaigns"),
        api.get("/marketing/reports/send-time-insights").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setRecent(camps.data.slice(0, 5));
      setInsights(ins.data);
    } catch (e) {
      toast.error("Failed to load overview");
    }
  };
  const reloadQueue = async () => {
    setQueueLoading(true);
    try {
      const r = await api.get("/marketing/queue?limit=200");
      setQueue(r.data);
    } catch (e) {
      toast.error("Failed to load queue");
    }
    setQueueLoading(false);
  };
  useEffect(() => { reload(); }, []);
  useEffect(() => { if (tab === "queue") reloadQueue(); }, [tab]);

  const runSync = async (kind) => {
    setSyncing(true);
    try {
      const r = await api.post(`/marketing/contacts/sync/${kind}`);
      toast.success(`${kind}: ${r.data.created} created, ${r.data.updated} updated`);
      reload();
    } catch (e) {
      toast.error(`Sync failed: ${e.response?.data?.detail || ""}`);
    }
    setSyncing(false);
  };

  return (
    <MarketingShell>
      <div className="flex items-center gap-1 border-b border-[#E5E5E5] mb-6 -mt-2" data-testid="overview-tabs">
        <button
          onClick={() => setTab("overview")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] border-b-2 transition-colors ${
            tab === "overview" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3] hover:text-[#525252]"
          }`}
          data-testid="tab-overview"
        >
          Overview
        </button>
        <button
          onClick={() => setTab("queue")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] border-b-2 transition-colors flex items-center gap-1.5 ${
            tab === "queue" ? "border-[#0055FF] text-[#0055FF]" : "border-transparent text-[#A3A3A3] hover:text-[#525252]"
          }`}
          data-testid="tab-queue"
        >
          <Clock size={12} weight="bold" />
          Queued Sends
          {queue.total > 0 && (
            <span className="ml-1 px-1.5 py-0.5 text-[9px] bg-[#0055FF] text-white rounded">{queue.total}</span>
          )}
        </button>
      </div>

      {tab === "queue" ? <QueueTab queue={queue} loading={queueLoading} onRefresh={reloadQueue} /> : (
      <>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
        <StatCard label="Total Contacts" value={stats?.total ?? "—"} sub={stats ? `${stats.suppressed} suppressed` : null} testid="stat-total" />
        <StatCard label="From CSV" value={stats?.by_source?.csv_import ?? "—"} sub="Imported / purchased" testid="stat-csv" />
        <StatCard label="From Clients" value={stats?.by_source?.client ?? "—"} sub="House records" testid="stat-clients" />
        <StatCard label="From Leads" value={stats?.by_source?.crm_lead ?? "—"} sub="Sales CRM" testid="stat-leads" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-8">
        <button onClick={() => nav("/marketing/campaigns/new")} className="bg-[#0055FF] hover:bg-[#0040CC] text-white text-left p-5 transition-colors" data-testid="action-new-campaign">
          <PaperPlaneTilt size={22} weight="fill" />
          <p className="text-xs uppercase tracking-[0.2em] font-bold mt-3 opacity-80">Create</p>
          <p className="text-lg font-bold">New Campaign</p>
        </button>
        <button onClick={() => nav("/marketing/imports/new")} className="bg-white border border-[#E5E5E5] hover:border-[#0055FF] text-left p-5 transition-colors" data-testid="action-import-csv">
          <Upload size={22} weight="bold" className="text-[#0055FF]" />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#A3A3A3] mt-3">Import</p>
          <p className="text-lg font-bold text-[#0A0A0A]">Upload CSV</p>
        </button>
        <div className="bg-white border border-[#E5E5E5] p-5">
          <ArrowsClockwise size={22} weight="bold" className="text-[#0055FF]" />
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#A3A3A3] mt-3">Sync</p>
          <p className="text-[13px] text-[#0A0A0A] mb-2">Rebuild contact index</p>
          <div className="flex gap-2">
            <button onClick={() => runSync("clients")} disabled={syncing} className="text-xs px-2 py-1 border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40" data-testid="action-sync-clients">Clients</button>
            <button onClick={() => runSync("leads")} disabled={syncing} className="text-xs px-2 py-1 border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40" data-testid="action-sync-leads">Leads</button>
          </div>
        </div>
      </div>

      {/* Send-time insights */}
      {insights && (
        <div className="bg-white border border-[#E5E5E5] p-5 mb-8" data-testid="send-time-insights">
          <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">Send-time optimization</p>
              <h2 className="text-base font-black tracking-tight text-[#0A0A0A]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                When your audience opens email
              </h2>
            </div>
            {insights.top_slots?.length > 0 && (
              <div className="text-right">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">Best slot</p>
                <p className="text-sm font-semibold text-[#0055FF]">
                  {insights.top_slots[0].dow} · {insights.top_slots[0].hour}:00 UTC
                </p>
              </div>
            )}
          </div>
          {insights.note && (
            <p className="text-xs text-[#D97706] mb-3">{insights.note}</p>
          )}
          {insights.total_opens > 0 ? (
            <HourHistogram data={insights.hour_hist} />
          ) : (
            <p className="text-xs text-[#A3A3A3]">No open events yet — insights will appear after your first sends.</p>
          )}
        </div>
      )}

      <div className="bg-white border border-[#E5E5E5]">
        <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Recent Campaigns</h2>
          <button onClick={() => nav("/marketing/campaigns")} className="text-[11px] font-semibold text-[#0055FF] hover:underline">View all</button>
        </div>        {recent.length === 0 ? (
          <p className="p-8 text-center text-sm text-[#A3A3A3]">No campaigns yet. Create your first to get started.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
              <tr>
                <th className="text-left px-5 py-2 font-bold">Name</th>
                <th className="text-left px-3 py-2 font-bold">Status</th>
                <th className="text-left px-3 py-2 font-bold">Subject</th>
                <th className="text-right px-3 py-2 font-bold">Recipients</th>
                <th className="text-right px-5 py-2 font-bold">Sent</th>
              </tr>
            </thead>
            <tbody>
              {recent.map(c => (
                <tr key={c.id} onClick={() => nav(`/marketing/campaigns/${c.id}`)} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA] cursor-pointer" data-testid={`recent-campaign-${c.id}`}>
                  <td className="px-5 py-3 font-semibold text-[#0A0A0A]">{c.name}</td>
                  <td className="px-3 py-3"><StatusChip status={c.status} /></td>
                  <td className="px-3 py-3 text-[#525252]">{c.subject || <span className="text-[#A3A3A3] italic">No subject</span>}</td>
                  <td className="px-3 py-3 text-right tabular-nums">{c.recipient_count ?? "—"}</td>
                  <td className="px-5 py-3 text-right text-xs text-[#A3A3A3]">{c.sent_at ? formatLocalDate(c.sent_at) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      </>
      )}
    </MarketingShell>
  );
}

function QueueTab({ queue, loading, onRefresh }) {
  const items = queue.items || [];
  return (
    <div className="bg-white border border-[#E5E5E5]" data-testid="queue-tab-body">
      <div className="px-5 py-3 border-b border-[#E5E5E5] flex items-center justify-between">
        <div>
          <h2 className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252]">Upcoming sends</h2>
          <p className="text-[11px] text-[#A3A3A3] mt-0.5">
            Workflow next-actions + deferred per-recipient campaign sends, in chronological order.
            Marketing emails respect Mon–Fri 09:30 in the recipient's local timezone (US federal holidays skipped).
          </p>
        </div>
        <button onClick={onRefresh} disabled={loading} className="text-xs px-3 py-1.5 border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40 inline-flex items-center gap-1.5" data-testid="queue-refresh-btn">
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>
      {items.length === 0 ? (
        <div className="p-12 text-center text-sm text-[#A3A3A3]" data-testid="queue-empty">
          <EnvelopeSimple size={32} weight="thin" className="mx-auto mb-2 text-[#CBD5E1]" />
          <p className="font-semibold text-[#525252]">No emails queued.</p>
          <p className="mt-1">Workflow drips and scheduled-window sends will appear here as they're queued.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="queue-table">
          <thead className="bg-[#FAFAFA] text-[10px] uppercase tracking-wider text-[#A3A3A3]">
            <tr>
              <th className="text-left px-4 py-2 font-bold whitespace-nowrap">When</th>
              <th className="text-left px-3 py-2 font-bold">Recipient</th>
              <th className="text-left px-3 py-2 font-bold">Source</th>
              <th className="text-left px-3 py-2 font-bold">Subject / Step</th>
              <th className="text-left px-3 py-2 font-bold whitespace-nowrap">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-t border-[#F5F5F5] hover:bg-[#FAFAFA]" data-testid={`queue-row-${it.id}`}>
                <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">
                  <div className="text-[#0A0A0A]">
                    {it.scheduled_for_local ? new Date(it.scheduled_for_local).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—"}
                    <span className="ml-1 text-[10px] text-[#A3A3A3] font-sans normal-case">{it.recipient_tz}</span>
                  </div>
                  <div className="text-[10px] text-[#A3A3A3] mt-0.5">UTC · {formatLocal(it.scheduled_for_utc)}</div>
                </td>
                <td className="px-3 py-3 text-[#525252]">
                  <div className="font-semibold text-[#0A0A0A]">{it.contact_email}</div>
                  {it.contact_name && <div className="text-[11px] text-[#A3A3A3]">{it.contact_name}</div>}
                </td>
                <td className="px-3 py-3">
                  {it.kind === "workflow" ? (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#F0F7FF] text-[#0055FF]">
                      <Stack size={10} weight="bold" /> Workflow
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider bg-[#F0FDF4] text-[#16A34A]">
                      <PaperPlaneTilt size={10} weight="bold" /> Campaign
                    </span>
                  )}
                  <div className="text-[11px] text-[#525252] mt-0.5 max-w-[180px] truncate">
                    {it.workflow_name || it.campaign_name || ""}
                  </div>
                </td>
                <td className="px-3 py-3 text-[#525252] max-w-[320px]">
                  <div className="font-medium truncate">{it.subject || <span className="text-[#A3A3A3] italic">{it.step_type}</span>}</div>
                  {it.step_index !== null && it.step_index !== undefined && (
                    <div className="text-[10px] text-[#A3A3A3] mt-0.5">Step {it.step_index} · {it.step_type}</div>
                  )}
                </td>
                <td className="px-3 py-3"><StatusChip status={it.status === "active" ? "scheduled" : it.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export function StatusChip({ status }) {  const cfg = {
    draft: { bg: "#F5F5F5", fg: "#525252", label: "Draft" },
    scheduled: { bg: "#EFF6FF", fg: "#0055FF", label: "Scheduled" },
    sending: { bg: "#FEF3C7", fg: "#92400E", label: "Sending" },
    sent: { bg: "#F0FDF4", fg: "#16A34A", label: "Sent" },
    paused: { bg: "#F5F5F5", fg: "#A3A3A3", label: "Paused" },
    archived: { bg: "#F5F5F5", fg: "#A3A3A3", label: "Archived" },
  }[status] || { bg: "#F5F5F5", fg: "#525252", label: status };
  return <span className="inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider" style={{ backgroundColor: cfg.bg, color: cfg.fg }}>{cfg.label}</span>;
}


function HourHistogram({ data }) {
  const max = Math.max(1, ...data.map(d => d.opens));
  return (
    <div className="flex items-end gap-[3px] h-20" data-testid="hour-histogram">
      {data.map(d => {
        const h = max > 0 ? Math.max(2, (d.opens / max) * 100) : 2;
        const bg = d.opens === max && max > 1 ? "#0055FF" : "#CBD5E1";
        return (
          <div key={d.hour} className="flex-1 flex flex-col items-center justify-end">
            <div className="w-full relative group" style={{ height: `${h}%`, backgroundColor: bg }}
                 title={`${d.hour}:00 — ${d.opens} opens`} />
            <span className="text-[9px] text-[#A3A3A3] mt-1 tabular-nums">{d.hour}</span>
          </div>
        );
      })}
    </div>
  );
}
